// Global API connection monitor. Wraps window.fetch once; tracks consecutive
// network-level failures against our backend and broadcasts up/down events so
// brief platform/ingress blips show a "reconnecting" banner instead of looking
// like a broken app. Only network errors (TypeError) and 502/503/504 count —
// 4xx/500 means the server answered, so the connection itself is fine.
import { recordClientFailure, flushClientTelemetry } from './clientTelemetry';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const FAILURE_THRESHOLD = 2;

let consecutiveFailures = 0;
let isDown = false;
let installed = false;

const emit = (down) => {
  if (down === isDown) return;
  isDown = down;
  window.dispatchEvent(new CustomEvent('api-connection', { detail: { down } }));
};

const recordFailure = () => {
  consecutiveFailures += 1;
  if (consecutiveFailures >= FAILURE_THRESHOLD) emit(true);
};

const recordSuccess = () => {
  consecutiveFailures = 0;
  const wasDown = isDown;
  emit(false);
  // Connection just recovered → upload the outage evidence buffered client-side.
  if (wasDown) setTimeout(flushClientTelemetry, 2000);
};

export const getConnectionDown = () => isDown;

export const installConnectionMonitor = () => {
  if (installed || !BACKEND_URL) return;
  installed = true;
  const realFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    const isBackend = url.startsWith(BACKEND_URL);
    const method = (init && init.method) || (typeof input === 'object' && input && input.method) || 'GET';
    const t0 = isBackend ? performance.now() : 0;
    try {
      const res = await realFetch(input, init);
      if (isBackend) {
        if ([502, 503, 504].includes(res.status)) recordFailure();
        else recordSuccess();
        if (res.status >= 500) {
          recordClientFailure({
            url, method, status: res.status, kind: 'http_error',
            message: res.statusText, durationMs: performance.now() - t0,
          });
        }
      }
      return res;
    } catch (err) {
      if (isBackend && err instanceof TypeError) {
        recordFailure();
        recordClientFailure({
          url, method, status: 0, kind: 'network_error',
          message: err.message || 'network failure (refused / TLS / DNS)',
          durationMs: performance.now() - t0,
        });
      }
      throw err;
    }
  };
  window.addEventListener('offline', () => emit(true));
  window.addEventListener('online', () => { consecutiveFailures = 0; emit(false); });
};
